import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Building2, Upload, Save, Loader2, Trash2, Mail, Phone,
  Globe, MessageCircle, MapPin, IdCard, User, FileText,
} from "lucide-react";

const EMPTY = {
  logo_data_url: null,
  nombre: "",
  cedula_juridica: "",
  representante_legal: "",
  representante_cedula: "",
  representante_email: "",
  descripcion: "",
  direccion: { provincia: "", canton: "", distrito: "", detalle: "" },
  contacto: { telefono: "", whatsapp: "", email: "", sitio_web: "" },
};

function Section({ title, icon: Icon, children }) {
  return (
    <section className="rounded-xl bg-white border border-[#E5DFD3] p-6">
      <div className="flex items-center gap-3 pb-4 border-b border-[#E5DFD3]">
        <div className="h-9 w-9 rounded-md bg-[#F5EEE0] border border-[#E5DFD3] flex items-center justify-center">
          <Icon className="h-4 w-4 text-[#7A1E2E]" strokeWidth={1.8} />
        </div>
        <h3 className="font-display text-lg text-[#1A0810]">{title}</h3>
      </div>
      <div className="pt-5 space-y-5">{children}</div>
    </section>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold flex items-center gap-1.5">
        {Icon && <Icon className="h-3 w-3" strokeWidth={1.8} />} {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

const inputCls = "h-11 bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]";

export default function BufeteTab() {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/despacho")
      .then((r) => setData({ ...EMPTY, ...r.data, direccion: { ...EMPTY.direccion, ...(r.data?.direccion || {}) }, contacto: { ...EMPTY.contacto, ...(r.data?.contacto || {}) } }))
      .catch((e) => toast.error("No se pudo cargar la información del bufete"))
      .finally(() => setLoading(false));
  }, []);

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setDir = (k, v) => setData((d) => ({ ...d, direccion: { ...d.direccion, [k]: v } }));
  const setCon = (k, v) => setData((d) => ({ ...d, contacto: { ...d.contacto, [k]: v } }));

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Debe ser una imagen"); return; }
    if (f.size > 1024 * 1024) { toast.error("El logo debe ser menor a 1 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setField("logo_data_url", reader.result);
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data: updated } = await api.put("/despacho", data);
      setData({ ...EMPTY, ...updated, direccion: { ...EMPTY.direccion, ...(updated?.direccion || {}) }, contacto: { ...EMPTY.contacto, ...(updated?.contacto || {}) } });
      toast.success("Información del bufete guardada");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-[#6B5D57]">Cargando información del bufete...</div>;

  return (
    <div className="space-y-6" data-testid="bufete-form">
      {/* Logo + brand */}
      <Section title="Identidad del bufete" icon={Building2}>
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          <div className="flex-shrink-0">
            <div className="h-32 w-32 rounded-xl bg-[#F5EEE0] border-2 border-dashed border-[#E5DFD3] flex items-center justify-center overflow-hidden">
              {data.logo_data_url ? (
                <img src={data.logo_data_url} alt="Logo del bufete" className="max-h-full max-w-full object-contain" />
              ) : (
                <Building2 className="h-10 w-10 text-[#C19B6C]" strokeWidth={1.4} />
              )}
            </div>
            <div className="mt-3 flex gap-2">
              <Button
                type="button" variant="outline" size="sm"
                data-testid="upload-logo-button"
                className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]"
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.8} /> Subir
              </Button>
              {data.logo_data_url && (
                <Button
                  type="button" variant="outline" size="sm"
                  data-testid="remove-logo-button"
                  className="border-[#E5DFD3] bg-white hover:bg-[#FBEDEF] text-[#7A1E2E]"
                  onClick={() => setField("logo_data_url", null)}
                >
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} />
                </Button>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="logo-file-input" />
            </div>
            <p className="mt-2 text-[11px] text-[#6B5D57]">PNG o JPG · máx 1 MB</p>
          </div>

          <div className="flex-1 w-full space-y-5">
            <Field label="Nombre del bufete" icon={Building2}>
              <Input
                data-testid="bufete-nombre"
                value={data.nombre} onChange={(e) => setField("nombre", e.target.value)}
                className={inputCls} placeholder="Bufete Vértice y Asociados"
              />
            </Field>
            <Field label="Descripción / lema" icon={FileText}>
              <Textarea
                data-testid="bufete-descripcion"
                value={data.descripcion} onChange={(e) => setField("descripcion", e.target.value)}
                className="min-h-[88px] bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]"
                placeholder="Breve descripción del bufete, especialidades o lema institucional."
              />
            </Field>
          </div>
        </div>
      </Section>

      {/* Información jurídica */}
      <Section title="Información jurídica" icon={IdCard}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Cédula jurídica">
            <Input data-testid="bufete-cedula" value={data.cedula_juridica} onChange={(e) => setField("cedula_juridica", e.target.value)} className={inputCls} placeholder="3-101-123456" />
          </Field>
          <Field label="Representante legal" icon={User}>
            <Input data-testid="bufete-representante" value={data.representante_legal} onChange={(e) => setField("representante_legal", e.target.value)} className={inputCls} placeholder="Lic. Andrea Solano" />
          </Field>
          <Field label="Cédula del representante">
            <Input data-testid="bufete-representante-cedula" value={data.representante_cedula} onChange={(e) => setField("representante_cedula", e.target.value)} className={inputCls} placeholder="1-1234-5678" />
          </Field>
          <Field label="Correo del abogado principal" icon={Mail}>
            <Input
              data-testid="bufete-representante-email"
              type="email"
              value={data.representante_email}
              onChange={(e) => setField("representante_email", e.target.value)}
              className={inputCls}
              placeholder="abogado.principal@bufete.com"
            />
            <p className="mt-1.5 text-[11px] text-[#6B5D57] leading-relaxed">
              Este correo es el <span className="font-semibold text-[#7A1E2E]">único que puede marcar escrituras como completadas</span> y eliminarlas del registro notarial.
            </p>
          </Field>
        </div>
      </Section>

      {/* Dirección */}
      <Section title="Dirección" icon={MapPin}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Field label="Provincia">
            <Input data-testid="bufete-provincia" value={data.direccion.provincia} onChange={(e) => setDir("provincia", e.target.value)} className={inputCls} placeholder="San José" />
          </Field>
          <Field label="Cantón">
            <Input data-testid="bufete-canton" value={data.direccion.canton} onChange={(e) => setDir("canton", e.target.value)} className={inputCls} placeholder="Escazú" />
          </Field>
          <Field label="Distrito">
            <Input data-testid="bufete-distrito" value={data.direccion.distrito} onChange={(e) => setDir("distrito", e.target.value)} className={inputCls} placeholder="San Rafael" />
          </Field>
        </div>
        <Field label="Dirección exacta">
          <Textarea
            data-testid="bufete-direccion-detalle"
            value={data.direccion.detalle} onChange={(e) => setDir("detalle", e.target.value)}
            className="min-h-[64px] bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]"
            placeholder="Del McDonald's 200m sur, edificio Vértice, piso 3, oficina 305"
          />
        </Field>
      </Section>

      {/* Contacto */}
      <Section title="Datos de contacto" icon={Phone}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Teléfono" icon={Phone}>
            <Input data-testid="bufete-telefono" value={data.contacto.telefono} onChange={(e) => setCon("telefono", e.target.value)} className={inputCls} placeholder="+506 2222-3333" />
          </Field>
          <Field label="WhatsApp" icon={MessageCircle}>
            <Input data-testid="bufete-whatsapp" value={data.contacto.whatsapp} onChange={(e) => setCon("whatsapp", e.target.value)} className={inputCls} placeholder="+506 8888-7777" />
          </Field>
          <Field label="Correo electrónico" icon={Mail}>
            <Input data-testid="bufete-email" type="email" value={data.contacto.email} onChange={(e) => setCon("email", e.target.value)} className={inputCls} placeholder="info@bufete.cr" />
          </Field>
          <Field label="Sitio web" icon={Globe}>
            <Input data-testid="bufete-sitio-web" value={data.contacto.sitio_web} onChange={(e) => setCon("sitio_web", e.target.value)} className={inputCls} placeholder="https://bufete.cr" />
          </Field>
        </div>
      </Section>

      <div className="sticky bottom-4 z-10 flex justify-end">
        <Button
          onClick={save} disabled={saving}
          data-testid="bufete-save-button"
          className="h-12 px-8 bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4] shadow-[0_10px_30px_-10px_rgba(43,9,19,0.4)]"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" strokeWidth={2} /> Guardar cambios</>}
        </Button>
      </div>
    </div>
  );
}
